import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Box,
  Chip,
  Paper,
  alpha,
  CircularProgress,
  Alert,
  Rating,
  Tooltip,
} from '@mui/material';
import PetsIcon from '@mui/icons-material/Pets';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import HikingIcon from '@mui/icons-material/Hiking';
import HotelIcon from '@mui/icons-material/Hotel';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import NatureIcon from '@mui/icons-material/Nature';
import { useNavigate } from 'react-router-dom';
import API from '../api/axios';
import { getImageUrl, FALLBACK_IMAGE } from '../utils/imageUtils';

const FALLBACK_PACKAGES = [
  {
    title: 'Ranthambore Tiger Safari',
    destination: 'Rajasthan, India',
    image_url: 'https://images.unsplash.com/photo-1561731216-c3a4d99437d5?auto=format&fit=crop&w=800&q=80',
    description: "Embark on an exciting tiger safari in one of India's premier national parks. Spot Bengal tigers in their natural habitat.",
    _id: 'f1',
  },
  {
    title: 'Kaziranga Rhino Expedition',
    destination: 'Assam, India',
    image_url: 'https://images.unsplash.com/photo-1549366021-9f761d450615?auto=format&fit=crop&w=800&q=80',
    description: 'Explore the home of the one-horned rhinoceros in the beautiful landscapes of Kaziranga National Park.',
    _id: 'f2',
  },
  {
    title: 'Jim Corbett Wildlife Tour',
    destination: 'Uttarakhand, India',
    image_url: 'https://images.unsplash.com/photo-1547970814-0e0e2cea4d4c?auto=format&fit=crop&w=800&q=80',
    description: "Visit India's oldest national park and experience the thrill of spotting diverse wildlife in the Himalayan foothills.",
    _id: 'f3',
  },
  {
    title: 'Bandhavgarh Tiger Reserve',
    destination: 'Madhya Pradesh, India',
    image_url: 'https://images.unsplash.com/photo-1574158622682-e40e69881006?auto=format&fit=crop&w=800&q=80',
    description: 'Known for having the highest density of tigers, Bandhavgarh offers excellent opportunities for wildlife photography.',
    _id: 'f4',
  },
  {
    title: 'Sundarbans Mangrove Adventure',
    destination: 'West Bengal, India',
    image_url: 'https://images.unsplash.com/photo-1582657118090-af35e8e4b935?auto=format&fit=crop&w=800&q=80',
    description: 'Cruise through the mysterious mangrove forests of Sundarbans, home to the majestic Royal Bengal Tiger.',
    _id: 'f5',
  },
  {
    title: 'Periyar Wildlife Sanctuary',
    destination: 'Kerala, India',
    image_url: 'https://images.unsplash.com/photo-1504173010664-32509aeebb62?auto=format&fit=crop&w=800&q=80',
    description: 'Experience the rich biodiversity of the Western Ghats with boat safaris and nature walks in Periyar.',
    _id: 'f6',
  },
];

const features = [
  { icon: <PetsIcon sx={{ fontSize: 36 }} />, title: 'Expert Naturalists', description: 'Guided by experienced naturalists who know the best spots for wildlife sightings.' },
  { icon: <CameraAltIcon sx={{ fontSize: 36 }} />, title: 'Photography Tours', description: 'Special photography-focused tours with guidance from professionals.' },
  { icon: <HikingIcon sx={{ fontSize: 36 }} />, title: 'Adventure Activities', description: 'Combine wildlife viewing with trekking, camping, and outdoor thrills.' },
  { icon: <HotelIcon sx={{ fontSize: 36 }} />, title: 'Eco-Friendly Stays', description: 'Stay at eco-lodges and resorts that support local conservation efforts.' },
];

const WildLifeTours = () => {
  const navigate = useNavigate();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);
  const heroRef = useRef(null);

  useEffect(() => {
    fetchWildlifeTours();
  }, []);

  const fetchWildlifeTours = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await API.get('/tours?category=Wildlife');
      const list = data.tours || (Array.isArray(data) ? data : []);
      if (list.length > 0) {
        setTours(list);
        setUsingFallback(false);
      } else {
        // No Wildlife tours in DB yet — show curated fallback packages
        setTours(FALLBACK_PACKAGES);
        setUsingFallback(true);
      }
    } catch {
      setTours(FALLBACK_PACKAGES);
      setUsingFallback(true);
    } finally {
      setLoading(false);
    }
  };

  const getRating = (tour) => {
    if (tour.rating) return Number(tour.rating);
    const idStr = String(tour._id || tour.id || '');
    const hash = idStr.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return 3.5 + (hash % 15) / 10;
  };

  const getLocation = (tour) => tour.destination || tour.location || null;

  const handleBook = (tour) => {
    if (tour._id && !String(tour._id).startsWith('f')) {
      navigate(`/booking?tourId=${tour._id}`);
    } else {
      navigate('/booking');
    }
  };

  return (
    <Box sx={{ bgcolor: '#f0fdf4', minHeight: '100vh', pb: 10 }}>
      {/* ─── Hero Section ─── */}
      <Box
        ref={heroRef}
        sx={{
          position: 'relative',
          height: '65vh',
          minHeight: 520,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage: 'url("https://images.unsplash.com/photo-1549366021-9f761d450615?auto=format&fit=crop&w=1920&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
            transform: 'scale(1.05)',
            transition: 'transform 12s ease-out',
            zIndex: 0,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,40,0,0.35) 0%, rgba(0,0,0,0.82) 100%)',
            zIndex: 1,
          },
          '&:hover::before': { transform: 'scale(1.1)' },
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
          {/* Badge */}
          <Box
            sx={{
              mb: 3,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'rgba(255,255,255,0.12)',
              backdropFilter: 'blur(12px)',
              px: 2.5,
              py: 0.9,
              borderRadius: 50,
              border: '1px solid rgba(255,255,255,0.25)',
              animation: 'fadeDown 0.8s ease',
              '@keyframes fadeDown': { from: { opacity: 0, transform: 'translateY(-16px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
            }}
          >
            <NatureIcon sx={{ fontSize: 18, color: '#4ade80' }} />
            <Typography variant="body2" sx={{ fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', fontSize: '0.75rem' }}>
              Explore The Untamed
            </Typography>
          </Box>

          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontWeight: 900,
              fontSize: { xs: '2.8rem', md: '4rem' },
              textShadow: '0 4px 30px rgba(0,0,0,0.6)',
              mb: 2,
              lineHeight: 1.1,
              animation: 'fadeUp 0.9s ease 0.1s both',
              '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(24px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
            }}
          >
            Wildlife Tours
          </Typography>

          <Typography
            variant="h6"
            sx={{
              mb: 5,
              fontWeight: 400,
              opacity: 0.88,
              maxWidth: 580,
              mx: 'auto',
              lineHeight: 1.7,
              fontSize: { xs: '1rem', md: '1.15rem' },
              animation: 'fadeUp 0.9s ease 0.2s both',
            }}
          >
            Experience India's incredible wildlife in their natural habitat with our expert-guided safari expeditions.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', animation: 'fadeUp 0.9s ease 0.3s both' }}>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/booking')}
              sx={{
                bgcolor: '#0C2340',
                color: 'white',
                px: 4,
                py: 1.6,
                borderRadius: 50,
                fontWeight: 700,
                fontSize: '1rem',
                textTransform: 'none',
                boxShadow: '0 10px 30px rgba(12,35,64,0.35)',
                '&:hover': { bgcolor: '#183B63', transform: 'translateY(-3px)', boxShadow: '0 16px 40px rgba(12,35,64,0.45)' },
                transition: 'all 0.3s ease',
              }}
            >
              Book Your Safari
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/contact')}
              sx={{
                borderColor: 'rgba(255,255,255,0.6)',
                color: 'white',
                px: 4,
                py: 1.6,
                borderRadius: 50,
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none',
                backdropFilter: 'blur(8px)',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.12)' },
                transition: 'all 0.3s ease',
              }}
            >
              Talk to an Expert
            </Button>
          </Box>
        </Container>

        {/* Decorative bottom wave */}
        <Box sx={{ position: 'absolute', bottom: -1, left: 0, right: 0, zIndex: 2 }}>
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: 60 }}>
            <path d="M0,40 C360,80 1080,0 1440,40 L1440,60 L0,60 Z" fill="#f0fdf4" />
          </svg>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 2 }}>
        {/* ─── Features Strip ─── */}
        <Grid container spacing={3} sx={{ mb: 10 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: 3.5,
                  height: '100%',
                  bgcolor: 'white',
                  borderRadius: 4,
                  border: '1px solid rgba(16,185,129,0.1)',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                  transition: 'all 0.35s cubic-bezier(0.25,0.8,0.25,1)',
                  animation: `fadeSlideUp 0.5s ease ${index * 0.08}s both`,
                  '@keyframes fadeSlideUp': { from: { opacity: 0, transform: 'translateY(20px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 50px rgba(16,185,129,0.14)',
                    borderColor: 'rgba(16,185,129,0.3)',
                    '& .icon-box': { bgcolor: '#10b981', color: 'white', transform: 'scale(1.1) rotate(5deg)' },
                  },
                }}
              >
                <Box
                  className="icon-box"
                  sx={{
                    color: '#10b981',
                    mb: 2.5,
                    display: 'inline-flex',
                    p: 1.8,
                    borderRadius: 3,
                    bgcolor: 'rgba(16,185,129,0.1)',
                    transition: 'all 0.4s cubic-bezier(0.175,0.885,0.32,1.275)',
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 800, color: '#1a1a2e', fontSize: '1rem' }}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {feature.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* ─── Packages Section ─── */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Chip
            icon={<NatureIcon sx={{ fontSize: '14px !important', color: '#10b981 !important' }} />}
            label="CURATED SAFARI EXPERIENCES"
            size="small"
            sx={{ mb: 2, fontWeight: 700, color: '#10b981', bgcolor: alpha('#10b981', 0.1), letterSpacing: 1, px: 2, py: 0.5, borderRadius: 50 }}
          />
          <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a1a2e', mb: 2, fontSize: { xs: '2rem', md: '2.5rem' } }}>
            Wildlife{' '}
            <Box component="span" sx={{ background: 'linear-gradient(135deg, #10b981 0%, #047857 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              Packages
            </Box>
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto', lineHeight: 1.7 }}>
            Choose from our premium wildlife safaris, designed for the ultimate nature enthusiast.
          </Typography>

          {usingFallback && !loading && (
            <Alert
              severity="info"
              icon={<NatureIcon />}
              sx={{ mt: 3, maxWidth: 560, mx: 'auto', borderRadius: 2, bgcolor: alpha('#10b981', 0.07), color: '#047857', border: '1px solid rgba(16,185,129,0.2)', '& .MuiAlert-icon': { color: '#10b981' } }}
            >
              Showing curated packages — add live Wildlife tours via <strong>Admin → Manage Tours</strong> to replace these.
            </Alert>
          )}
        </Box>

        {/* Packages Grid */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
            <CircularProgress sx={{ color: '#10b981' }} size={48} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ maxWidth: 500, mx: 'auto', borderRadius: 3 }}>{error}</Alert>
        ) : (
          <Grid container spacing={4}>
            {tours.map((pkg, index) => (
              <Grid item xs={12} sm={6} lg={4} key={pkg._id || pkg.id || index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 4,
                    bgcolor: 'white',
                    border: '1px solid rgba(0,0,0,0.05)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                    transition: 'all 0.4s cubic-bezier(0.25,0.8,0.25,1)',
                    overflow: 'hidden',
                    animation: `fadeSlideUp 0.5s ease ${index * 0.07}s both`,
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: '0 24px 50px rgba(16,185,129,0.15)',
                      '& .card-media': { transform: 'scale(1.08)' },
                    },
                  }}
                >
                  {/* Image */}
                  <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                    <CardMedia
                      className="card-media"
                      component="img"
                      height="240"
                      image={getImageUrl(pkg.imageUrl || pkg.image_url) || FALLBACK_IMAGE}
                      alt={pkg.title}
                      onError={(e) => { e.target.src = FALLBACK_IMAGE; }}
                      sx={{ transition: 'transform 0.6s ease', objectFit: 'cover' }}
                    />
                    {/* Gradient overlay with title */}
                    <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.82), transparent)', p: 2.5, pt: 5 }}>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 800, lineHeight: 1.2, textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
                        {pkg.title}
                      </Typography>
                    </Box>
                    {/* Wildlife badge */}
                    <Box sx={{ position: 'absolute', top: 14, left: 14 }}>
                      <Chip
                        icon={<NatureIcon sx={{ fontSize: '14px !important', color: 'white !important' }} />}
                        label="Wildlife"
                        size="small"
                        sx={{ bgcolor: '#10b981', color: 'white', fontWeight: 700, fontSize: '0.65rem', height: 24, backdropFilter: 'blur(8px)' }}
                      />
                    </Box>
                  </Box>

                  <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
                    {/* Meta row */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                      {getLocation(pkg) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                          <LocationOnIcon sx={{ fontSize: 15, color: '#10b981' }} />
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>{getLocation(pkg)}</Typography>
                        </Box>
                      )}
                      {pkg.maxPeople && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                          <PeopleIcon sx={{ fontSize: 15, color: '#10b981' }} />
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>Up to {pkg.maxPeople}</Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Rating */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Rating value={getRating(pkg)} precision={0.5} size="small" readOnly sx={{ '& .MuiRating-iconFilled': { color: '#f59e0b' } }} />
                      <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: 700 }}>{getRating(pkg).toFixed(1)}</Typography>
                    </Box>

                    {/* Description */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3, lineHeight: 1.7, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                    >
                      {pkg.description}
                    </Typography>

                    {/* Highlights */}
                    {Array.isArray(pkg.highlights) && pkg.highlights.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: 3 }}>
                        {pkg.highlights.slice(0, 4).map((h, i) => (
                          <Chip
                            key={i}
                            label={h}
                            size="small"
                            sx={{ bgcolor: alpha('#10b981', 0.1), color: '#047857', fontWeight: 600, fontSize: '0.68rem', borderRadius: 1.5 }}
                          />
                        ))}
                      </Box>
                    )}

                    {/* CTA */}
                    <Box sx={{ mt: 'auto', pt: 2, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                      <Tooltip title={usingFallback ? 'This is a sample package. Real tours will have direct booking.' : ''} placement="top" disableHoverListener={!usingFallback}>
                        <Button
                          fullWidth
                          variant="outlined"
                          endIcon={<ArrowForwardIcon />}
                          onClick={() => handleBook(pkg)}
                          sx={{
                            borderRadius: 50,
                            color: '#10b981',
                            borderColor: alpha('#10b981', 0.4),
                            fontWeight: 700,
                            textTransform: 'none',
                            py: 1,
                            '&:hover': { bgcolor: '#10b981', color: 'white', borderColor: '#10b981', boxShadow: '0 6px 18px rgba(16,185,129,0.3)' },
                            transition: 'all 0.3s ease',
                          }}
                        >
                          {usingFallback ? 'Enquire Now' : 'Book This Safari'}
                        </Button>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* ─── International Clients CTA Banner ─── */}
        <Box
          sx={{
            mt: 10,
            borderRadius: 6,
            overflow: 'hidden',
            position: 'relative',
            background: 'linear-gradient(135deg, #10b981 0%, #047857 100%)',
            color: 'white',
            boxShadow: '0 24px 48px rgba(16,185,129,0.35)',
          }}
        >
          {/* Decorative shape */}
          <Box sx={{ position: 'absolute', top: '-20%', right: '-10%', width: '50%', height: '150%', bgcolor: 'rgba(255,255,255,0.05)', transform: 'rotate(-15deg)', pointerEvents: 'none' }} />
          <Box sx={{ position: 'absolute', bottom: '-30%', left: '-5%', width: '30%', height: '120%', bgcolor: 'rgba(255,255,255,0.04)', transform: 'rotate(10deg)', pointerEvents: 'none' }} />

          <Box
            sx={{
              p: { xs: 4, md: 7 },
              position: 'relative',
              zIndex: 2,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              gap: 5,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Chip label="FOR GLOBAL TRAVELERS" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700, letterSpacing: 1, mb: 2 }} />
              <Typography variant="h3" gutterBottom sx={{ fontWeight: 900, mb: 2, lineHeight: 1.2, fontSize: { xs: '1.8rem', md: '2.4rem' } }}>
                International Wildlife Enthusiasts
              </Typography>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 400, opacity: 0.88, fontSize: '1.05rem' }}>
                Tailor-made safari experiences for global travelers
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, opacity: 0.82, lineHeight: 1.85 }}>
                We provide end-to-end customized wildlife tours including visa assistance, luxury airport transfers, premium eco-lodge accommodations, and multilingual expert guides. Let us handle the logistics while you focus on the adventure.
              </Typography>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/contact')}
                sx={{
                  bgcolor: 'white',
                  color: '#047857',
                  borderRadius: 50,
                  px: 4,
                  py: 1.5,
                  fontWeight: 800,
                  textTransform: 'none',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.92)', transform: 'translateX(5px)', boxShadow: '0 12px 30px rgba(0,0,0,0.2)' },
                  transition: 'all 0.3s ease',
                }}
              >
                Contact Our Specialists
              </Button>
            </Box>
            <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }}>
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=600&q=80"
                alt="Wildlife experience"
                sx={{
                  width: '100%',
                  borderRadius: 5,
                  boxShadow: '0 24px 50px rgba(0,0,0,0.25)',
                  transform: 'rotate(2deg)',
                  transition: 'transform 0.5s ease',
                  '&:hover': { transform: 'rotate(0deg) scale(1.02)' },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default WildLifeTours;